// React
import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
// import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';

// Components & Style
import GuardedComponent from '@components/molecules/GuardedComponent/GuardedComponent';
import { COORDINATOR } from '@constants/roles';

// Formik + Yup
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import {
  fetchIndicatorTemplate as fetchIndicatorTemplateService,
  createIndicatorTemplate as createIndicatorTemplateService,
  updateIndicatorTemplate as updateIndicatorTemplateService,
  deleteIndicatorTemplate as deleteIndicatorTemplateService,
} from '@services/indicatorTemplateServices';
import { connect } from 'react-redux';

// Data
import {
  indicatorCategory,
  indicatorType,
  indicatorNumericalType,
  indicatorTimePeriod,
  agregationLevel,
} from '@constants/selectLists';

// UI
import { Grid } from '@mui/material';
import Input from '@components/atoms/Input/Input';
import Select from '@components/atoms/Select/Select';
import EditButton from '@components/atoms/EditButton/EditButton';
import Button from '@components/atoms/Button/Button';
import DialogConfirm from '@components/templates/DialogConfirm/DialogConfirm';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import {
  StyledWrapper,
  StyledTitle,
  StyledHeading,
  StyledButton,
} from './IndicatorTemplateSettings.styles';

const AddIndicatorTemplate = ({
  fetchIndicatorTemplate,
  createIndicatorTemplate,
  updateIndicatorTemplate,
  deleteIndicatorTemplate,
  details,
  clearIndicatorTemplate,
}) => {
  const { id } = useParams();
  const history = useHistory();

  const [isEditable, setIsEditable] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  useEffect(() => {
    clearIndicatorTemplate();
  }, []);

  const triggerFetchIndicator = useCallback(
    (myid) => {
      if (myid !== undefined) {
        return fetchIndicatorTemplate(myid);
      }

      setIsEditable(true);

      return true;
    },
    [fetchIndicatorTemplate],
  );

  useEffect(() => {
    triggerFetchIndicator(id);
  }, [id, triggerFetchIndicator]);

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      indicator_name: values.indicator_name,
      indicator_category: values.indicator_category,
      indicator_kind: 'INDICATOR_CONTEST',
      indicator_type: values.indicator_type,
      indicator_time_period: values.indicator_time_period,
      description: values.description,
      agregationLevel: values.agregationLevel,
      type_details: values.typeDetails,
      planned_number_of_measurements: values.measurementNumber,
    };
    const actionPromise = id
      ? updateIndicatorTemplate(id, payload)
      : createIndicatorTemplate(payload);
    actionPromise
      .then((res) => {
        setSubmitting(false);
        if (!id) {
          const newId = res && res.data && res.data.id;
          if (newId) history.push(`/konfiguracja/wzorce-wskaznikow/${newId}`);
          else history.push('/konfiguracja');
        }
        setIsEditable(false);
      })
      .catch((err) => {
        console.error(err);
        setSubmitting(false);
      });
  };

  const handleDelete = () => {
    console.log('Deleting indicator template with ID:', id);
    deleteIndicatorTemplate(id).then(() => history.push('/konfiguracja/tab/5-wzorce-wskaznikow'));
  };

  return (
    <>
      {/* <BreadcrumbsItem to="/konfiguracja/tab/5-wzorce-wskaznikow">
        Wzorce wskaźników
      </BreadcrumbsItem> */}
      {/* <BreadcrumbsItem to={id ? `/wzorce-wskaznikow/${id}` : '/wzorce-wskaznikow-dodaj'} isActive>
        {id ? 'Edytuj wzorzec' : 'Dodaj wzorzec'}
      </BreadcrumbsItem> */}
      <GuardedComponent allowed_user_roles={[COORDINATOR]}>
        <StyledTitle>
          <StyledHeading>
            {id ? 'Edytuj wzorzec wskaźnika' : 'Dodaj wzorzec wskaźnika'}
          </StyledHeading>
          {id && <EditButton isEditable={isEditable} setIsEditable={setIsEditable} />}
        </StyledTitle>
        <Formik
          initialValues={{
            indicator_name: details.indicator_name || '',
            indicator_category: details.indicator_category || '',
            indicator_type: details.indicator_type || '',
            indicator_time_period: details.indicator_time_period || '',
            description: details.description || '',
            agregationLevel: details.agregationLevel || '',
            typeDetails: details.typeDetails ||
              details.type_details || {
                select: [],
                numerical: { from: '', to: '', type: '' },
              },
            measurementNumber:
              details.measurementNumber || details.planned_number_of_measurements || '',
          }}
          enableReinitialize
          validationSchema={Yup.object({
            indicator_name: Yup.string().required('pole wymagane!'),
            indicator_category: Yup.string().required('pole wymagane!'),
            indicator_type: Yup.string().required('pole wymagane!'),
            indicator_time_period: Yup.string().required('pole wymagane!'),
            description: Yup.string().required('pole wymagane!'),
            agregationLevel: Yup.string().required('pole wymagane!'),
          })}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, setFieldValue, handleSubmit: formikSubmit, isSubmitting }) => (
            <StyledWrapper container spacing={2}>
              <Grid item xs={6}>
                <Input
                  disabled={!isEditable}
                  label="Nazwa"
                  name="indicator_name"
                  value={values.indicator_name}
                  onChange={handleChange}
                  fullWidth
                />
                <ErrorMessage name="indicator_name" component="div" className="error-txt" />
              </Grid>
              <Grid item xs={6}>
                <Select
                  disabled={!isEditable}
                  label="Kategoria"
                  data={indicatorCategory}
                  selectValue={values.indicator_category}
                  text="name"
                  onChange={(_e, v) => setFieldValue('indicator_category', v)}
                  fullWidth
                />
                <ErrorMessage name="indicator_category" component="div" className="error-txt" />
              </Grid>
              <Grid item xs={6}>
                <Select
                  disabled={!isEditable}
                  label="Typ pomiaru"
                  data={indicatorType}
                  selectValue={values.indicator_type}
                  text="name"
                  onChange={(_e, v) => setFieldValue('indicator_type', v)}
                  fullWidth
                />
                <ErrorMessage name="indicator_type" component="div" className="error-txt" />
              </Grid>
              <Grid item xs={6}>
                <Select
                  disabled={!isEditable}
                  label="Przedział czasowy"
                  data={indicatorTimePeriod}
                  selectValue={values.indicator_time_period}
                  text="name"
                  onChange={(_e, v) => setFieldValue('indicator_time_period', v)}
                  fullWidth
                />
                <ErrorMessage name="indicator_time_period" component="div" className="error-txt" />
              </Grid>
              <Grid item xs={12}>
                <Input
                  disabled={!isEditable}
                  label="Opis"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  fullWidth
                />
                <ErrorMessage name="description" component="div" className="error-txt" />
              </Grid>
              <Grid item xs={6}>
                <Select
                  disabled={!isEditable}
                  label="Agregacja"
                  data={agregationLevel}
                  selectValue={values.agregationLevel}
                  text="agregationLevel"
                  onChange={(_e, v) => setFieldValue('agregationLevel', v)}
                  variant="filled"
                  fullWidth
                />
                <ErrorMessage name="agregationLevel" component="div" className="error-txt" />
              </Grid>
              {/* Ustaw parametry wskaźnika */}
              <Grid item xs={12}>
                <StyledHeading>Ustaw parametry wskaźnika</StyledHeading>
              </Grid>
              <Grid item xs={6}>
                <Select
                  disabled={!isEditable}
                  label="Rodzaj pomiaru"
                  data={indicatorType}
                  text="indicator_type"
                  selectValue={values.indicator_type}
                  onChange={setFieldValue}
                  variant="filled"
                />
              </Grid>
              {values.indicator_type === 'INDICATOR_SELECT' && (
                <Grid item container xs={6} spacing={2}>
                  <StyledHeading>Wartości pola wyboru</StyledHeading>
                  {values.typeDetails.select.map((el, index) => (
                    <Grid key={el.id} item container xs={12} spacing={2}>
                      <Grid item xs={4}>
                        <Input
                          label="Wartość"
                          variant="filled"
                          type="text"
                          onChange={handleChange}
                          name={`typeDetails.select[${index}].value`}
                          value={values.typeDetails.select[index].value}
                          disabled={!isEditable}
                        />
                      </Grid>
                      <Grid item xs={7}>
                        <Input
                          label="Opis"
                          variant="filled"
                          type="text"
                          onChange={handleChange}
                          name={`typeDetails.select[${index}].name`}
                          value={values.typeDetails.select[index].name}
                          disabled={!isEditable}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <StyledButton
                          startIcon={<RemoveCircleIcon />}
                          disabled={!isEditable}
                          onClick={() => {
                            const arr = [...values.typeDetails.select];
                            arr.splice(index, 1);
                            setFieldValue('typeDetails.select', arr);
                          }}
                        >
                          usuń
                        </StyledButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <StyledButton
                      startIcon={<AddCircleIcon />}
                      disabled={!isEditable}
                      onClick={() => {
                        const arr = [...values.typeDetails.select];
                        arr.push({ value: '', name: '', id: arr.length + 1 });
                        setFieldValue('typeDetails.select', arr);
                      }}
                    >
                      Dodaj kolejny
                    </StyledButton>
                  </Grid>
                </Grid>
              )}
              {values.indicator_type === 'INDICATOR_NUMERICAL' && (
                <>
                  <Grid item xs={2}>
                    <StyledHeading>Zakres</StyledHeading>
                    <Input
                      label="Od"
                      variant="filled"
                      type="text"
                      name="typeDetails.numerical.from"
                      value={values.typeDetails.numerical.from}
                      onChange={handleChange}
                      disabled={!isEditable}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <StyledHeading>
                      <br />
                    </StyledHeading>
                    <Input
                      label="Do"
                      variant="filled"
                      type="text"
                      name="typeDetails.numerical.to"
                      value={values.typeDetails.numerical.to}
                      onChange={handleChange}
                      disabled={!isEditable}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <StyledHeading>Wartości</StyledHeading>
                    <Select
                      label="rodzaj"
                      data={indicatorNumericalType}
                      variant="filled"
                      onChange={setFieldValue}
                      text="typeDetails.numerical.type"
                      selectValue={values.typeDetails.numerical.type}
                      disabled={!isEditable}
                    />
                  </Grid>
                </>
              )}
              {values.indicator_type === 'INDICATOR_YESNO' && <Grid item xs={6} />}
              <Grid item xs={6}>
                <Select
                  label="Przedział czasowy pomiaru"
                  data={indicatorTimePeriod}
                  selectValue={values.indicator_time_period}
                  text="indicator_time_period"
                  onChange={setFieldValue}
                  variant="filled"
                  disabled={!isEditable}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  disabled={!isEditable}
                  label="Liczba pomiarów"
                  name="measurementNumber"
                  value={values.measurementNumber}
                  onChange={handleChange}
                  variant="filled"
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledButton
                  onClick={formikSubmit}
                  disabled={!isEditable || isSubmitting}
                  variant="contained"
                >
                  {id ? 'Zapisz' : 'Dodaj'}
                </StyledButton>
                {id && (
                  <Button
                    color="secondary"
                    onClick={() => setOpenDelete(true)}
                    style={{ marginLeft: '1rem' }}
                  >
                    Usuń
                  </Button>
                )}
              </Grid>
            </StyledWrapper>
          )}
        </Formik>
        <DialogConfirm
          open={openDelete}
          title="Usuń wzorzec wskaźnika"
          body={<div>{`Czy na pewno chcesz usunąć wzorzec '${details.indicator_name}'?`}</div>}
          setOpenFn={setOpenDelete}
          removeFunction={handleDelete}
        />
      </GuardedComponent>
    </>
  );
};
AddIndicatorTemplate.propTypes = {
  fetchIndicatorTemplate: PropTypes.func,
  createIndicatorTemplate: PropTypes.func,
  updateIndicatorTemplate: PropTypes.func,
  deleteIndicatorTemplate: PropTypes.func,
  clearIndicatorTemplate: PropTypes.func,
  details: PropTypes.shape({
    indicator_name: PropTypes.string,
    indicator_category: PropTypes.string,
    indicator_type: PropTypes.string,
    indicator_time_period: PropTypes.string,
    description: PropTypes.string,
    agregationLevel: PropTypes.string,
    typeDetails: PropTypes.shape({
      select: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          value: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      numerical: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
        type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
    type_details: PropTypes.shape({
      select: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          value: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      numerical: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
        type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
    measurementNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planned_number_of_measurements: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
AddIndicatorTemplate.defaultProps = {
  fetchIndicatorTemplate: null,
  createIndicatorTemplate: null,
  updateIndicatorTemplate: null,
  deleteIndicatorTemplate: null,
  clearIndicatorTemplate: null,
  details: {
    indicator_name: '',
    indicator_category: '',
    indicator_type: '',
    indicator_time_period: '',
    description: '',
    agregationLevel: '',
    typeDetails: { select: [], numerical: { from: '', to: '', type: '' } },
    measurementNumber: '',
  },
};

const mapStateToProps = ({ indicatorTemplateReducer }) => ({
  details: indicatorTemplateReducer.indicatorTemplate,
});

const mapDispatchToProps = (dispatch) => ({
  fetchIndicatorTemplate: (id) => dispatch(fetchIndicatorTemplateService(id)),
  createIndicatorTemplate: (data) => dispatch(createIndicatorTemplateService(data)),
  updateIndicatorTemplate: (id, data) => dispatch(updateIndicatorTemplateService(id, data)),
  deleteIndicatorTemplate: (id) => dispatch(deleteIndicatorTemplateService(id)),
  clearIndicatorTemplate: () => dispatch({ type: 'CLEAR_INDICATOR_TEMPLATE' }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddIndicatorTemplate);
